import type { FullPageWidget } from '@luminovo/apps-types';

import type { AddressableSlotAssignment } from '../api/fetchSlotAssignments';
import { matchWidgetRoute } from './matchWidgetRoute';

export type FullPageSlotAssignment = Omit<AddressableSlotAssignment, 'widget'> & { widget: FullPageWidget };

export class FullPageSlotConfigurationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'FullPageSlotConfigurationError';
    }
}

/**
 * Several widgets claimed the same pathname, so the tenant is misconfigured and specificity
 * picked the winner. Handed back as DATA rather than reported from here: matching runs
 * during render, and a render-phase side effect fires twice under `React.StrictMode`, fires
 * again whenever React drops a `useMemo` cache, and is unsound even when it happens not to.
 * The caller reports it from an effect — see `FullPageSlots`.
 */
export interface FullPageAssignmentCollision {
    pathname: string;
    /** Every competing `app.id:widget.id`, most-specific-first, so the winner is the first. */
    competingWidgets: string[];
    /** The competing routes, in the same order as `competingWidgets`. */
    competingRoutes: string[];
}

/** A widget owns this URL and the params it declared are usable. */
export interface MatchedFullPageAssignment {
    status: 'matched';
    assignment: FullPageSlotAssignment;
    /** Decoded path params, plus any declared param the widget takes from the query string. */
    params: Record<string, string>;
    /** Set only when more than one widget claimed the pathname. */
    collision?: FullPageAssignmentCollision;
}

/**
 * A widget owns this URL, but the params it needs are missing or cannot be decoded.
 *
 * Deliberately NOT folded into `no-match`: the URL still belongs to an app page, so
 * the host keeps its chrome around the failure instead of demoting the page to its
 * own 404. Only the caller can tell those two apart, which is why this is a third
 * outcome rather than a `null`.
 */
export interface UnusableFullPageAssignment {
    status: 'invalid-params';
    assignment: FullPageSlotAssignment;
    error: FullPageSlotConfigurationError;
    /** Set only when more than one widget claimed the pathname. */
    collision?: FullPageAssignmentCollision;
}

/** No widget claims this URL — it belongs to the product's own no-match handling. */
export interface NoFullPageAssignmentMatch {
    status: 'no-match';
}

export type FullPageAssignmentMatch =
    | MatchedFullPageAssignment
    | UnusableFullPageAssignment
    | NoFullPageAssignmentMatch;

interface Candidate {
    assignment: FullPageSlotAssignment;
    /** The captured params, still encoded as the pathname spells them. */
    rawParams: Record<string, string>;
    /**
     * The tiebreaker of last resort. Stable across responses in a way the position in
     * `assignments` is not: the slot-assignment API promises no ordering, so ranking on
     * array index would let two responses carrying the same data render different pages.
     */
    key: string;
}

/**
 * The stable identity of a widget placement. Also the render identity the host gives the
 * widget frame, so navigating from one widget to another remounts the frame instead of
 * handing the next widget the previous one's loading state.
 */
export const fullPageWidgetKey = (assignment: FullPageSlotAssignment): string =>
    `${assignment.app.id}:${assignment.widget.id}`;

/**
 * Orders two widget keys (`${app.id}:${widget.id}`), the tiebreak whenever two widgets
 * claim one place. Lexicographic, not `localeCompare`: the winner must not depend on
 * the browser's locale either.
 */
export const compareWidgetKeys = (a: string, b: string): number => (a === b ? 0 : a < b ? -1 : 1);

const routeSegments = (route: string): string[] => route.split('/').filter((segment) => segment.length > 0);

const isDynamic = (segment: string): boolean => segment.startsWith(':');
const isWildcard = (segment: string): boolean => segment === '*';

const staticSegmentCount = (segments: string[]): number =>
    segments.filter((segment) => !isDynamic(segment) && !isWildcard(segment)).length;

const concreteSegmentCount = (segments: string[]): number => segments.filter((segment) => !isWildcard(segment)).length;

// Static segments constrain a route most, params constrain one segment, and a
// trailing wildcard accepts everything left. Used only after static-count ranking.
const segmentSpecificity = (segment: string): number => (isWildcard(segment) ? 0 : isDynamic(segment) ? 1 : 2);

/**
 * Orders two widget routes most-specific-first. See the resolution contract on
 * `fullPageWidgetSchema.route` in `@luminovo/apps-types` for the authoritative wording.
 *
 * Rules, applied in order:
 *  1. more static segments wins — `/apps/first/:id` over `/apps/:app/:id`;
 *  2. otherwise the leftmost more-constraining segment wins: static, then `:param`, then `*`;
 *  3. with wildcards, more concrete segments wins — `/apps/:id/settings/*` over `/apps/:id/*`.
 *
 * Anything still tied returns 0 deliberately. Parameter NAME length never participates:
 * `/apps/:app/:contact` must not beat `/apps/:a/:c`, or renaming a manifest param would flip
 * which widget renders. `compareCandidates` breaks what is left on the stable widget key.
 */
const compareRouteSpecificity = (a: string, b: string): number => {
    const aSegments = routeSegments(a);
    const bSegments = routeSegments(b);

    const byStaticCount = staticSegmentCount(bSegments) - staticSegmentCount(aSegments);
    if (byStaticCount !== 0) return byStaticCount;

    for (let index = 0; index < Math.min(aSegments.length, bSegments.length); index++) {
        const bySegment = segmentSpecificity(bSegments[index]) - segmentSpecificity(aSegments[index]);
        if (bySegment !== 0) return bySegment;
    }

    const byConcreteCount = concreteSegmentCount(bSegments) - concreteSegmentCount(aSegments);
    if (byConcreteCount !== 0) return byConcreteCount;

    // The same concrete route without a wildcard claims a narrower URL set.
    const aHasWildcard = aSegments.some(isWildcard);
    const bHasWildcard = bSegments.some(isWildcard);
    if (aHasWildcard !== bHasWildcard) return aHasWildcard ? 1 : -1;

    return 0;
};

const compareCandidates = (a: Candidate, b: Candidate): number => {
    const bySpecificity = compareRouteSpecificity(a.assignment.widget.route, b.assignment.widget.route);
    if (bySpecificity !== 0) return bySpecificity;

    return compareWidgetKeys(a.key, b.key);
};

const decodeAndValidateParams = (
    widget: FullPageWidget,
    rawParams: Record<string, string>,
    search: string,
): Record<string, string> => {
    // `rawParams` holds the route's declared params only. The wildcard remainder is not captured: it
    // selects widget UI through the widget's own `useLocation`.
    const params: Record<string, string> = Object.fromEntries(
        Object.entries(rawParams).map(([key, value]) => {
            try {
                return [key, decodeURIComponent(value)];
            } catch {
                throw new FullPageSlotConfigurationError(`Invalid encoding for route parameter "${key}"`);
            }
        }),
    );

    // A widget may take a param from the query string instead of a path segment, so
    // anything it DECLARED and the path did not supply is looked up in `search`. The
    // declaration is the filter: without it every query key on the URL would be
    // forwarded into the frame. Path params stay authoritative — the URL's own
    // structure outranks a query key that happens to share a name.
    const searchParams = new URLSearchParams(search);
    for (const key of Object.keys(widget.paramsSchema.properties ?? {})) {
        if (params[key] !== undefined) continue;
        const value = searchParams.get(key);
        if (value !== null) params[key] = value;
    }

    for (const requiredParam of widget.paramsSchema.required ?? []) {
        if (!params[requiredParam]) {
            throw new FullPageSlotConfigurationError(
                `Widget "${widget.id}" requires route parameter "${requiredParam}"`,
            );
        }
    }
    return params;
};

const describeCollision = (pathname: string, ranked: Candidate[]): FullPageAssignmentCollision => ({
    pathname,
    competingWidgets: ranked.map((candidate) => candidate.key),
    competingRoutes: ranked.map((candidate) => candidate.assignment.widget.route),
});

/**
 * The error a host reports for a collision. Built here rather than at the call site so the
 * wording stays next to the data it describes — and building an `Error` is not a side effect,
 * which is what lets this stay in a module the matcher can be called from during render.
 */
export const fullPageCollisionError = (collision: FullPageAssignmentCollision): FullPageSlotConfigurationError =>
    new FullPageSlotConfigurationError(
        `Multiple full-page widgets match "${collision.pathname}": ${collision.competingWidgets.join(', ')} — ` +
            `rendering "${collision.competingWidgets[0]}"`,
    );

/**
 * Resolves a pathname against the tenant's full-page slot assignments.
 *
 * The one matcher for full-page slots: it decides which widget owns a URL, decodes the
 * params that widget gets, and is the only place the ambiguity policy lives.
 *
 * When several widgets claim the same pathname the tenant is misconfigured, and this
 * resolves it rather than failing: the most specific route wins (`compareRouteSpecificity`),
 * ties break on `app.id:widget.id`, and the collision comes back on the result as `collision`
 * for the caller to report. The customer keeps a working page; we find out. Notably the winner
 * does NOT depend on the order of `assignments`, which the API does not promise to keep stable.
 *
 * Params are validated for the winner only. Specificity decides who owns the URL, so a
 * misconfigured params schema cannot hand the page to a different widget — it surfaces as
 * `invalid-params` on the widget that actually owns the route.
 *
 * `search` is the query string with its `?` (`stringifySearch` of the router's search), needed
 * because a declared param may come from the query string rather than from a path segment.
 *
 * Pure, and it has to stay that way: callers run it during render.
 */
export function matchFullPageAssignment(
    assignments: readonly AddressableSlotAssignment[],
    pathname: string,
    search: string,
): FullPageAssignmentMatch {
    const candidates = assignments.flatMap((assignment): Candidate[] => {
        if (assignment.widget.kind !== 'full-page') {
            return [];
        }
        const fullPageAssignment = assignment as FullPageSlotAssignment;
        const rawParams = matchWidgetRoute(fullPageAssignment.widget.route, pathname);
        if (!rawParams) {
            return [];
        }
        return [
            {
                assignment: fullPageAssignment,
                rawParams,
                key: fullPageWidgetKey(fullPageAssignment),
            },
        ];
    });

    if (candidates.length === 0) {
        return { status: 'no-match' };
    }

    const ranked = candidates.sort(compareCandidates);
    const winner = ranked[0];
    const collision = ranked.length > 1 ? describeCollision(pathname, ranked) : undefined;

    try {
        return {
            status: 'matched',
            assignment: winner.assignment,
            params: decodeAndValidateParams(winner.assignment.widget, winner.rawParams, search),
            collision,
        };
    } catch (error) {
        if (error instanceof FullPageSlotConfigurationError) {
            return { status: 'invalid-params', assignment: winner.assignment, error, collision };
        }
        throw error;
    }
}
